// ---------------------------------------------------------------------------
// spintaxEngine.js
// Resolves {option1|option2|option3} spintax using a seeded RNG.
// Supports nested spintax up to 10 levels deep.
// ---------------------------------------------------------------------------

/**
 * Resolve all spintax groups in a string using the provided SeededRNG.
 * Handles nested spintax recursively.
 */
export const resolveSpintax = (text, rng) => {
  if (!text || typeof text !== 'string') return text;

  let result = text;
  let maxPasses = 10;

  while (maxPasses-- > 0 && result.includes('{') && result.includes('|')) {
    result = resolveOnePass(result, rng);
  }

  return result;
};

const resolveOnePass = (text, rng) => {
  // Find innermost {a|b|c} groups (no nested braces inside)
  return text.replace(/\{([^{}]+)\}/g, (_, inner) => {
    const options = inner.split('|');
    return options[Math.floor(rng._next() * options.length)];
  });
};

/**
 * Count how many unique combinations a spintax string can produce.
 * Capped at 1,000,000 for display purposes.
 */
export const countSpintaxCombinations = (text) => {
  if (!text) return 1;
  let total = 1;
  const matches = text.match(/\{([^{}]+)\}/g) || [];
  for (const m of matches) {
    const opts = m.slice(1, -1).split('|').length;
    total *= opts;
    if (total > 1_000_000) return 1_000_000;
  }
  return total;
};

/**
 * Validate spintax structure — checks for balanced braces.
 */
export const validateSpintax = (text) => {
  let depth = 0;
  for (const ch of text) {
    if (ch === '{') depth++;
    else if (ch === '}') depth--;
    if (depth < 0) return { valid: false, error: 'Unmatched closing brace }' };
  }
  if (depth !== 0) return { valid: false, error: `${depth} unclosed brace(s) {` };
  return { valid: true, error: null };
};